"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.routingImportResultSchema = exports.importProblemSchema = exports.importTableResultSchema = exports.importFacilityResultSchema = exports.importRowOutcome = exports.crosswalkMirrorSchema = exports.mirrorFacilitySchema = exports.routingImportQuerySchema = exports.routingBundleSchema = exports.crosswalkRowSchema = exports.bundleContentLineSchema = exports.bundleStandingCcSchema = exports.bundleEndpointSchema = exports.bundleContactSchema = exports.bundleUnitSchema = exports.bundleFacilitySchema = void 0;
exports.normaliseFacilityName = normaliseFacilityName;
exports.crosswalkCandidates = crosswalkCandidates;
/**
 * `RPM-104` — the routing import: the bundle, the crosswalk a person signs off, and what a dry run
 * reports (`J-488`).
 *
 * THE BUNDLE IS THE REFERENCE APP'S OWN TABLES, in their own names and lower-case codes — the shape
 * `docs/rpm/source/communication-routing/synthetic-tables.mjs` builds — with the signed crosswalk
 * beside them. It is kept outside the repository and sent in the request body; nothing in it is PHI.
 *
 * THE CROSSWALK LINKS NOTHING BY ITSELF (`CR-24`). `crosswalkCandidates` only says, for each reference
 * facility, whether exactly one mirror building's normalised name or official name equals its name;
 * a person decides `LINK` or `SKIP`, and only a signed `LINK` loads.
 */
const zod_1 = require("zod");
const HOUR = zod_1.z.number().int().min(0).max(24).nullable();
const LOCAL_TIME = zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "a local time as HH:MM");
exports.bundleFacilitySchema = zod_1.z.object({
    facility_id: zod_1.z.string().min(1),
    name: zod_1.z.string(),
    behavior_code: zod_1.z.string(),
    text_preferred: zod_1.z.boolean(),
    summary_time: zod_1.z.string(),
    behavior_needs_review: zod_1.z.boolean(),
    routing_notes: zod_1.z.string(),
});
exports.bundleUnitSchema = zod_1.z.object({
    unit_id: zod_1.z.string().min(1),
    facility_id: zod_1.z.string().min(1),
    unit_label: zod_1.z.string(),
    rooms: zod_1.z.string(),
});
exports.bundleContactSchema = zod_1.z.object({
    contact_id: zod_1.z.string().min(1),
    facility_id: zod_1.z.string().min(1),
    unit_id: zod_1.z.string(),
    label: zod_1.z.string(),
    scope: zod_1.z.enum(["facility", "unit"]),
});
exports.bundleEndpointSchema = zod_1.z.object({
    endpoint_id: zod_1.z.string().min(1),
    contact_id: zod_1.z.string().min(1),
    channel: zod_1.z.enum(["email", "phone"]),
    value: zod_1.z.string(),
    phone_kind: zod_1.z.string(),
    window: zod_1.z.enum(["all_times", "day_9_5", "night"]),
    callable: zod_1.z.union([zod_1.z.boolean(), zod_1.z.literal("")]),
    summary_only: zod_1.z.boolean(),
    ext: zod_1.z.string(),
    days: zod_1.z.string(),
    start_hour: HOUR,
    end_hour: HOUR,
    flag: zod_1.z.string(),
});
exports.bundleStandingCcSchema = zod_1.z.object({
    scope: zod_1.z.enum(["alert", "summary"]),
    address: zod_1.z.string(),
    label: zod_1.z.string(),
});
exports.bundleContentLineSchema = zod_1.z.object({
    facility_id: zod_1.z.string(),
    notification_type: zod_1.z.enum(["alert", "daily_summary", "weekly_summary"]),
    attribute_text: zod_1.z.string(),
});
/** One signed crosswalk row: which mirror building a reference facility is, and the person's call. */
exports.crosswalkRowSchema = zod_1.z.object({
    reference_facility_id: zod_1.z.string().min(1),
    mirror_facility_id: zod_1.z.string().max(20).nullable(),
    decision: zod_1.z.enum(["LINK", "SKIP"]),
    signed_by: zod_1.z.string().min(1),
    signed_at: zod_1.z.iso.datetime({ offset: true }),
    /** The daily summary's local times the sign-off confirms; a free-text schedule loads only as these. */
    summary_times: zod_1.z.array(LOCAL_TIME).max(24),
});
exports.routingBundleSchema = zod_1.z
    .strictObject({
    facilities: zod_1.z.array(exports.bundleFacilitySchema),
    units: zod_1.z.array(exports.bundleUnitSchema),
    contacts: zod_1.z.array(exports.bundleContactSchema),
    endpoints: zod_1.z.array(exports.bundleEndpointSchema),
    standing_ccs: zod_1.z.array(exports.bundleStandingCcSchema),
    notification_attributes: zod_1.z.array(exports.bundleContentLineSchema),
    crosswalk: zod_1.z.array(exports.crosswalkRowSchema),
})
    .meta({
    id: "RoutingBundle",
    description: "The reference routing tables and the signed crosswalk",
});
exports.routingImportQuerySchema = zod_1.z.object({ dryRun: zod_1.z.enum(["true", "false"]) });
/** A mirror building, as the candidates are matched against it. */
exports.mirrorFacilitySchema = zod_1.z.object({
    facilityId: zod_1.z.string(),
    name: zod_1.z.string(),
    officialName: zod_1.z.string().nullable(),
});
exports.crosswalkMirrorSchema = zod_1.z
    .object({ items: zod_1.z.array(exports.mirrorFacilitySchema) })
    .meta({ id: "CrosswalkMirror", description: "The mirror's buildings a crosswalk can name" });
/** A name as the crosswalk compares it: lower case, letters and digits, single spaces. */
function normaliseFacilityName(name) {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}
/** `RPM-104` step 1: each reference facility's match against the mirror. It links nothing. */
function crosswalkCandidates(facilities, mirror) {
    return facilities.map((f) => {
        const wanted = normaliseFacilityName(f.name);
        const ids = mirror
            .filter((m) => wanted !== "" &&
            (normaliseFacilityName(m.name) === wanted ||
                (m.officialName !== null && normaliseFacilityName(m.officialName) === wanted)))
            .map((m) => m.facilityId);
        return {
            referenceFacilityId: f.facility_id,
            referenceName: f.name,
            match: ids.length === 1 ? "EXACT" : ids.length === 0 ? "NONE" : "AMBIGUOUS",
            mirrorFacilityIds: ids,
        };
    });
}
/** What the import does with one row: load it, load it held, leave it out, or refuse it. */
exports.importRowOutcome = zod_1.z.enum(["LOAD", "HELD", "LEFT_OUT", "REFUSED", "SKIPPED"]);
exports.importFacilityResultSchema = zod_1.z.object({
    referenceFacilityId: zod_1.z.string(),
    mirrorFacilityId: zod_1.z.string().nullable(),
    outcome: exports.importRowOutcome,
    reason: zod_1.z.string().nullable(),
    /** The building has no current profile with a zone (`RPM-028`), so it needs one. */
    needsProfile: zod_1.z.boolean(),
});
exports.importTableResultSchema = zod_1.z.object({
    table: zod_1.z.string(),
    load: zod_1.z.number().int(),
    held: zod_1.z.number().int(),
    leftOut: zod_1.z.number().int(),
    refused: zod_1.z.number().int(),
});
exports.importProblemSchema = zod_1.z.object({
    table: zod_1.z.string(),
    row: zod_1.z.string(),
    outcome: exports.importRowOutcome,
    reason: zod_1.z.string(),
});
exports.routingImportResultSchema = zod_1.z
    .object({
    dryRun: zod_1.z.boolean(),
    /** A bundle failing validation applies nothing; `errors` names the failing rows. */
    valid: zod_1.z.boolean(),
    applied: zod_1.z.boolean(),
    errors: zod_1.z.array(zod_1.z.object({ table: zod_1.z.string(), row: zod_1.z.string(), message: zod_1.z.string() })),
    facilities: zod_1.z.array(exports.importFacilityResultSchema),
    tables: zod_1.z.array(exports.importTableResultSchema),
    /** Every row held, left out or refused, with why. */
    rows: zod_1.z.array(exports.importProblemSchema),
})
    .meta({
    id: "RoutingImportResult",
    description: "What the routing import would load, or loaded",
});
//# sourceMappingURL=rpm-routing-import.js.map